/**
 * What this visitor has looked at, in this visit.
 *
 * A "session interest vector" is a running, weighted tally of the tags a buyer
 * has engaged with -- view a 1950s Omega and hover a vintage Rolex and the
 * vector reads roughly `{mechanical-watches: 1.3, vintage: 0.8, 1950s: 0.5}`.
 * It is a vector because those numbers can be compared against other things: a
 * category group, a listing's own tags, a candidate row. That comparison is
 * what orders the page.
 *
 * WHY IN-SESSION, and not a stored profile: 95,211 of roughly 98,000 anonymous
 * visitors in 30 days appear on exactly ONE day. A cross-visit profile would be
 * empty for almost everyone. The median session sees 3-5 distinct tags, so this
 * has to be useful from the second listing viewed and cannot assume more.
 *
 * WHY NOT SERVER-SIDE: the tags a buyer views are already sent to ClickHouse,
 * but reading them back costs a round trip and they are minutes stale. The page
 * needs the vector before its first fetch, so the browser keeps its own copy.
 * ClickHouse remains the system of record for everything analytical; this is a
 * fast local mirror for ordering, nothing more.
 *
 * Everything here degrades to "no signal" rather than throwing. Blocked storage
 * (Safari private mode throws on the accessor itself) leaves the in-memory copy
 * working for the rest of the page's life.
 */

import { FACET_AFFINITY_FIELDS } from './facet-affinity';

/** How much each kind of engagement says about intent. */
export const INTEREST_WEIGHTS = {
  /** Opened the listing. The strongest thing a browsing buyer does. */
  view: 1,
  /** Paused on the card. Real but much weaker -- and far noisier. */
  hover: 0.3,
  /** Stepped through the photos. Deliberate, and rarer than a hover. */
  photos: 0.5,
  /**
   * Applied a brand, size, colour, gender or material filter. The tokens are
   * `<field>:<value>` (utils/facet-affinity.ts), matched against the listing's
   * attribute fields rather than its tags. Stronger than a view: it is the
   * buyer saying what they want, not what caught their eye.
   */
  facet: 1.5,
} as const;

export type InterestSignal = keyof typeof INTEREST_WEIGHTS;

/**
 * Per-observation decay, applied by age in observations rather than by clock.
 *
 * Deliberately gentle. With a median of 3-5 distinct tags per session, an
 * aggressive decay would throw away most of the (already thin) evidence and
 * make the ordering lurch on every click. At 0.9 the tenth-most-recent
 * observation still carries about a third of its weight, so this mostly ranks
 * by frequency and uses recency to break ties.
 */
const OBSERVATION_DECAY = 0.9;

/**
 * Observations kept. Far above the median session, small enough that the whole
 * thing serialises cheaply on every write.
 */
const MAX_OBSERVATIONS = 50;

/** Tags per observation. Listings average 2.72 tags; this is just a sanity bound. */
const MAX_TAGS_PER_OBSERVATION = 12;

const STORAGE_KEY = 'sw_session_interest';

interface Observation {
  tags: string[];
  categoryGroupId?: number;
  weight: number;
}

/** In-memory copy. Authoritative during a page's life; storage is the mirror. */
let observations: Observation[] = [];
let loaded = false;

function readStorage(): Observation[] {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((o): o is Observation => Array.isArray(o?.tags) && typeof o?.weight === 'number')
      .slice(-MAX_OBSERVATIONS);
  } catch {
    return [];
  }
}

function writeStorage(): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(observations));
  } catch {
    // Blocked storage: the in-memory copy still serves this page view, which is
    // the case that matters for ordering the screen in front of the buyer.
  }
}

/** Hydrate once per page load. Cheap, and idempotent. */
function ensureLoaded(): void {
  if (loaded) return;
  loaded = true;
  // Both callers return early without a window, so this only runs in the browser.
  observations = readStorage();
}

/**
 * Record that the buyer engaged with a listing.
 *
 * Safe to call on every hover: a listing with no tags contributes nothing, and
 * the buffer is bounded.
 */
export function recordInterest(params: {
  signal: InterestSignal;
  tags?: string[] | null;
  categoryGroupId?: number | string | null;
}): void {
  if (typeof window === 'undefined') return;
  ensureLoaded();

  const tags = (params.tags || [])
    .filter((t): t is string => typeof t === 'string' && t.length > 0)
    .map(t => t.toLowerCase())
    .slice(0, MAX_TAGS_PER_OBSERVATION);

  const groupId = Number(params.categoryGroupId);
  const categoryGroupId = Number.isFinite(groupId) && groupId > 0 ? groupId : undefined;

  // Nothing to learn from.
  if (tags.length === 0 && categoryGroupId === undefined) return;

  observations.push({
    tags,
    categoryGroupId,
    weight: INTEREST_WEIGHTS[params.signal],
  });
  if (observations.length > MAX_OBSERVATIONS) {
    observations = observations.slice(-MAX_OBSERVATIONS);
  }

  writeStorage();
}

export interface SessionInterest {
  /** Tag -> weight, highest first. Empty when the session has no signal yet. */
  tags: Array<{ tag: string; weight: number }>;
  /** Category group -> weight, highest first. */
  categoryGroups: Array<{ categoryGroupId: number; weight: number }>;
  /** How many observations back this. 0 means cold start. */
  observationCount: number;
}

/**
 * The vector, as of now.
 *
 * Weights are computed at read time from the observation list rather than
 * accumulated in place, so the decay is a pure function of what was seen and
 * there is no stored number drifting out of step with it.
 */
export function getSessionInterest(): SessionInterest {
  if (typeof window === 'undefined') {
    return { tags: [], categoryGroups: [], observationCount: 0 };
  }
  ensureLoaded();

  const tagWeights = new Map<string, number>();
  const groupWeights = new Map<number, number>();
  const newest = observations.length - 1;

  observations.forEach((observation, index) => {
    const recency = Math.pow(OBSERVATION_DECAY, newest - index);
    const weight = observation.weight * recency;

    for (const tag of observation.tags) {
      tagWeights.set(tag, (tagWeights.get(tag) || 0) + weight);
    }
    if (observation.categoryGroupId !== undefined) {
      groupWeights.set(
        observation.categoryGroupId,
        (groupWeights.get(observation.categoryGroupId) || 0) + weight
      );
    }
  });

  return {
    tags: [...tagWeights.entries()]
      .map(([tag, weight]) => ({ tag, weight }))
      .sort((a, b) => b.weight - a.weight),
    categoryGroups: [...groupWeights.entries()]
      .map(([categoryGroupId, weight]) => ({ categoryGroupId, weight }))
      .sort((a, b) => b.weight - a.weight),
    observationCount: observations.length,
  };
}

/**
 * Reorder category groups by what this session has shown interest in.
 *
 * STABLE, and deliberately conservative: groups the buyer has engaged with move
 * to the front in weight order, and everything else keeps its original relative
 * order behind them. A session with no signal gets the input back untouched,
 * which is what makes this safe to apply unconditionally on a cold start.
 *
 * Only groups present in `defaultOrder` can appear -- the home page excludes
 * some groups entirely, and interest in a Pets listing must not smuggle Pets
 * onto the front page.
 */
export function rankCategoryGroups(defaultOrder: number[]): number[] {
  const { categoryGroups } = getSessionInterest();
  if (categoryGroups.length === 0) return [...defaultOrder];

  const allowed = new Set(defaultOrder);
  const ranked = categoryGroups
    .map(g => g.categoryGroupId)
    .filter(id => allowed.has(id));

  if (ranked.length === 0) return [...defaultOrder];

  const promoted = new Set(ranked);
  return [...ranked, ...defaultOrder.filter(id => !promoted.has(id))];
}

/**
 * How well a listing's tags match this session, as a small non-negative number.
 *
 * Used to re-rank WITHIN a row, never to filter: a listing that matches nothing
 * scores 0 and keeps its place rather than disappearing. Normalised by the
 * vector's own total weight so the scale does not drift as a session gets
 * longer.
 */
export function scoreTagsAgainstInterest(
  tags: string[] | null | undefined,
  interest?: SessionInterest
): number {
  const vector = interest ?? getSessionInterest();
  const tagVector = vector.tags.filter(t => !isFacetToken(t.tag));
  if (!tags || tags.length === 0 || tagVector.length === 0) return 0;

  const weights = new Map(tagVector.map(t => [t.tag, t.weight]));
  const total = tagVector.reduce((sum, t) => sum + t.weight, 0);
  if (total <= 0) return 0;

  let matched = 0;
  for (const tag of tags) {
    matched += weights.get(String(tag).toLowerCase()) || 0;
  }
  return matched / total;
}

/**
 * The head of the vector, shaped for a server query.
 *
 * The home page sends this to /api/search/home-showcases so each row is
 * ordered by overlap on the server -- the same factor For You rows get from
 * the ClickHouse vector, here fed from what this visit has shown. Weights are
 * the decayed tallies as they stand; only their ratios matter to the script.
 */
export function sessionAffinityTags(limit = 12): Array<{ tag: string; weight: number }> {
  return getSessionInterest()
    .tags.slice(0, Math.max(0, limit))
    .map(({ tag, weight }) => ({ tag, weight }));
}

/**
 * Re-rank a row's listings by how much of this session's interest each covers.
 *
 * STABLE: listings the vector says nothing about keep their server order, in
 * a block after the ones it does -- so a cold session gets the row back in
 * exactly the order it arrived, and a warm one sees what it has been looking
 * at moved to the front without anything being filtered. Meant to run ONCE,
 * when a row's listings arrive; running it on every render would shuffle
 * cards under the buyer's cursor as their interest changes.
 */
export function rankListingsByInterest<T extends { tags?: string[] | null }>(
  listings: readonly T[],
  interest?: SessionInterest
): T[] {
  const vector = interest ?? getSessionInterest();
  if (listings.length < 2 || vector.tags.length === 0) return [...listings];

  return listings
    .map((listing, index) => ({ listing, index, score: scoreTagsAgainstInterest(listing.tags, vector) }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(entry => entry.listing);
}

/**
 * Facet tokens (`brand:nike`) only match attribute fields on the server, so the
 * client-side re-rank above, which compares against `listing.tags`, ignores
 * them: it scores tags only, and its denominator is the tag weight alone.
 */
export function isFacetToken(tag: string): boolean {
  return FACET_AFFINITY_FIELDS.some(field => tag.startsWith(`${field}:`));
}

/** Test seam. */
export function resetSessionInterestForTests(): void {
  observations = [];
  loaded = false;
}
